// 화면 18 — Leader Stocks 45 Grid (Phase B) 메인 페이지.
// 라우트: /leading/stocks
// 권위: ARCH_DASHBOARD §4.18 / §6.2 (v1.10.3), PRD_Dashboard §5.8.
// 폴링 300s. WebSocket 미사용 (ADR-DASH-06).
//
// v1.10.3:
//   - diff 의 lookback 은 정수 days (1 | 5 | 30) — URL 파라미터도 정수로 단순화 (호환을 위해 'D-1'/'5d'/'30d' 도 수용).
//   - snapshot 의 config_version 제거.

import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ApiError } from '../../api/client';
import { LeaderStocksGrid } from './LeaderStocksGrid';
import { DiffPanel } from './DiffPanel';
import { BandHistoryDrawer } from './BandHistoryDrawer';
import { useLeaderStocksDaily, useLeaderStocksDiff } from './useLeadingStocks';
import {
  daysToLabel,
  labelToDays,
  todayYmd,
  type LookbackDays,
  type LookbackLabel,
} from './types';
import './leadingStocks.css';

const LOOKBACK_DAYS_VALUES: readonly LookbackDays[] = [1, 5, 30] as const;
const LOOKBACK_LABEL_VALUES: readonly LookbackLabel[] = ['D-1', '5d', '30d'] as const;

function parsePrevDays(v: string | null): LookbackDays {
  if (v == null) return 1;
  // 정수 호환.
  const n = Number(v);
  if ((LOOKBACK_DAYS_VALUES as readonly number[]).includes(n)) return n as LookbackDays;
  // 라벨 호환 (예전 URL).
  if ((LOOKBACK_LABEL_VALUES as readonly string[]).includes(v)) {
    return labelToDays(v as LookbackLabel);
  }
  return 1;
}

export default function LeadingStocksPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();

  const dateParam = searchParams.get('date');
  // URL 호환: 새 'prev' 또는 구 'lookback' 파라미터에서 정수 추출.
  const prevParam = searchParams.get('prev') ?? searchParams.get('lookback');
  const prevDays = parsePrevDays(prevParam);

  // 기본 일자 — URL 미지정 시 null → 백엔드가 max(trading_date) 자동 폴백.
  // diff 의 baseDate 는 daily 응답의 snapshot trading_date 를 사용 (휴장 폴백과 동기화).
  const date = dateParam ?? null;

  const setDate = useCallback(
    (next: string) => {
      const sp = new URLSearchParams(searchParams);
      sp.set('date', next);
      setSearchParams(sp, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const setPrevDays = useCallback(
    (next: LookbackDays) => {
      const sp = new URLSearchParams(searchParams);
      sp.set('prev', String(next));
      // 구 lookback 파라미터가 남아있으면 제거.
      sp.delete('lookback');
      setSearchParams(sp, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const daily = useLeaderStocksDaily(date);
  // diff baseDate = daily 응답의 실제 trading_date (서버 폴백 결과와 동기화).
  const diffBaseDate = daily.data?.trading_date ?? null;
  const diff = useLeaderStocksDiff(diffBaseDate, prevDays);

  const newInSymbols = useMemo<ReadonlySet<string>>(() => {
    if (!diff.data) return new Set<string>();
    return new Set(diff.data.new_in.map((e) => e.symbol));
  }, [diff.data]);

  // 드로어 상태 (band history).
  const [drawerSymbol, setDrawerSymbol] = useState<string | null>(null);
  const openDrawer = useCallback((s: string) => setDrawerSymbol(s), []);
  const closeDrawer = useCallback(() => setDrawerSymbol(null), []);

  // 응답 trading_date 가 입력과 다르면 (휴장 fallback 등) URL 자동 동기화 안 함 — 사용자 의도 보존.
  // 단, snapshot trading_date 를 헤더에 표시.
  const snapshotDate = daily.data?.trading_date ?? null;
  const pipelineMode = daily.data?.pipeline_mode ?? null;

  // 메타 — 갱신 중 표시.
  const isFetching = daily.isFetching || diff.isFetching;

  // 날짜 입력 max — 오늘.
  const todayStr = useMemo(() => todayYmd(), []);

  // 응답이 422 / 404 등의 경우 사용자에게 표시.
  const dailyError = daily.isError ? (daily.error as ApiError) : null;

  // URL date 미지정 시 서버가 max(trading_date) 폴백 — URL 에 today 박지 않음
  // (사용자 의도 보존 + 휴장일 안전).

  return (
    <div className="lst-page" data-testid="leading-stocks-page">
      <header className="lst-page__header">
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '16px', flexWrap: 'wrap' }}>
          <h1 className="lst-page__title">LeaderStocks45 그리드</h1>
          <span className="lst-muted">
            Phase B · 3 섹터 × 15 종목 일별 스냅샷 + In/Out diff + 밴드 타임라인
            {isFetching && ' · 갱신 중…'}
          </span>
        </div>
        <div className="lst-header-right">
          {snapshotDate && (
            <span className="lst-muted">
              최근 일자: <code>{snapshotDate}</code>
              {pipelineMode && (
                <>
                  {' '}
                  · mode: <code>{pipelineMode}</code>
                </>
              )}
            </span>
          )}
        </div>
      </header>

      <div className="lst-filterbar">
        <label className="lst-filter__label" htmlFor="lst-date">
          기준일
        </label>
        <input
          id="lst-date"
          type="date"
          className="lst-filter__input"
          value={date ?? snapshotDate ?? ''}
          max={todayStr}
          onChange={(e) => setDate(e.target.value)}
          data-testid="lst-date-input"
        />
        <span className="lst-muted">· 폴링 300s</span>
      </div>

      {dailyError && (
        <div className="lst-banner lst-banner--error" data-testid="lst-daily-error">
          {dailyError.httpStatus === 404
            ? `${date ?? '최근'} 일자 LeaderStocks45 row 가 없습니다 (휴장 또는 미적재).`
            : `LeaderStocks45 로드 실패: ${dailyError.message}`}
        </div>
      )}

      {/* In/Out diff (상단 — D-1 / 5d / 30d 토글) */}
      <section className="lst-panel" aria-labelledby="lst-diff-h">
        <div className="lst-panel__header">
          <h2 className="lst-panel__title" id="lst-diff-h">
            In / Out Diff
          </h2>
          <span className="lst-panel__hint">
            NEW IN · DROPPED OUT · HELD ·{' '}
            {diff.data &&
              `${diff.data.counts.new_in}/${diff.data.counts.dropped_out}/${diff.data.counts.held}`}{' '}
            · <code>{daysToLabel(prevDays)}</code>
          </span>
        </div>
        <DiffPanel
          data={diff.data}
          isLoading={diff.isLoading}
          error={diff.isError ? (diff.error as ApiError) : null}
          prevDays={prevDays}
          onPrevDaysChange={setPrevDays}
          onSymbolClick={openDrawer}
        />
      </section>

      {/* 45 종목 그리드 (중단) */}
      <section className="lst-panel" aria-labelledby="lst-grid-h">
        <div className="lst-panel__header">
          <h2 className="lst-panel__title" id="lst-grid-h">
            45 종목 그리드 (3 섹터 × 15)
          </h2>
          <span className="lst-panel__hint">
            {daily.data ? `${daily.data.leader_stocks.length} row` : '…'}
          </span>
        </div>
        <LeaderStocksGrid
          rows={daily.data?.leader_stocks ?? []}
          newInSymbols={newInSymbols}
          onSymbolClick={openDrawer}
          isLoading={daily.isLoading}
        />
      </section>

      <BandHistoryDrawer
        open={drawerSymbol != null}
        symbol={drawerSymbol}
        onClose={closeDrawer}
      />
    </div>
  );
}
